"use client";

import { memo } from "react";
import { useScenePause } from "./useScenePause";

/**
 * Animated SVG illustration for the /residences flow tile on the landing
 * page.
 *
 * Reads as "residences, compared by price per m²": four complexes stand
 * on one street line as a skyline that is also a bar chart, each tower as
 * tall as its average AZN/m². The towers BUILD UP from the ground one
 * after another (the blockBuild scaleY with a slight overshoot that the
 * tool illustrations use), then a selection hops between three of them:
 * the picked tower turns solid indigo, its name bar darkens and an ink
 * tooltip springs in with its price per m², the same tooltip MapAnimation
 * shows over a hex. The tour ends on the cheapest per m².
 *
 * No real data: the prices are illustrative and the towers are the
 * window-grid building glyph of AnalyzeAnimation and ToolAnimations.
 *
 * Conventions match the sibling flow animations: 200x160 viewBox, design
 * tokens via var(...) for the surface (auto-flip in dark mode) and the
 * mid-indigo hex for the buildings, transform/opacity-only motion, a 7s
 * loop with a fade at the seam, memo() wrapped, and a reduced-motion
 * fallback that lands on the final frame (towers up, the last stop
 * picked, its tooltip visible).
 */
function ResidencesAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const GROUND = 132; // the street line every tower stands on
  const TW = 28;      // tower width
  const TIP_W = 68;
  const TIP_H = 18;

  const C_RULE = "var(--color-rule)";
  const C_INK = "var(--color-ink)";
  const C_PAPER = "var(--color-paper)";
  const INDIGO = "#6366f1";

  // Height = price per m² / 32, so the dearest complex stands tallest;
  // `name` is the width of the grey name bar under the tower.
  const towers = [
    { x: 20, ppm: 1700, name: 16 },
    { x: 64, ppm: 2400, name: 22 },
    { x: 108, ppm: 1900, name: 18 },
    { x: 152, ppm: 2900, name: 20 },
  ].map((t) => ({ ...t, top: GROUND - Math.round(t.ppm / 32) }));
  // The tour, as tower indexes: middle-left, far right, then the cheapest.
  const STOPS = [1, 3, 0];
  const money = (n: number) => `${String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")}\u00a0AZN/m²`;

  // A 3-wide window grid, one row every 8 units, a plain base above the
  // ground line.
  const windows = (x: number, top: number) => {
    const out: { x: number; y: number }[] = [];
    for (let y = top + 6; y + 4.5 <= GROUND - 8; y += 8) {
      for (const dx of [5, 11.75, 18.5]) out.push({ x: x + dx, y });
    }
    return out;
  };

  return (
    <svg ref={sceneRef} viewBox="0 0 200 160" className="scene-svg w-full h-44" aria-hidden="true">
      {/* street line (always visible, anchors the scene mid-cycle) */}
      <line x1="12" y1={GROUND} x2="188" y2={GROUND} stroke={C_RULE} strokeWidth="1" />

      {/* Towers. The outer group carries the pick (pale until the tour
          reaches it), the inner one the build-up from the ground; the
          name bar under the street line darkens with the pick. */}
      {towers.map((t, i) => {
        const stop = STOPS.indexOf(i);
        return (
          <g key={i} className={stop >= 0 ? `rs-pick rs-pick-${stop}` : "rs-pick"}>
            <g className={`rs-rise rs-rise-${i}`}>
              <rect x={t.x} y={t.top} width={TW} height={GROUND - t.top} rx="2.5" fill={INDIGO} />
              {windows(t.x, t.top).map((w) => (
                <rect key={`${w.x}-${w.y}`} x={w.x} y={w.y} width="4.5" height="4.5" rx="0.8" fill="#ffffff" fillOpacity="0.9" />
              ))}
            </g>
            <rect x={t.x + (TW - t.name) / 2} y={GROUND + 7} width={t.name} height="5" rx="2.5" fill={C_INK} fillOpacity="0.5" />
          </g>
        );
      })}

      {/* Per-stop tooltips: ink on paper, the price per m² of the picked
          tower, springing in while it is picked. */}
      {STOPS.map((ti, j) => {
        const t = towers[ti];
        const cx = t.x + TW / 2;
        const tipX = cx - TIP_W / 2;
        const tipY = t.top - TIP_H - 8;
        return (
          <g key={j} className={`rs-tip rs-tip-${j}`}>
            <rect x={tipX} y={tipY} width={TIP_W} height={TIP_H} rx="2" fill={C_INK} />
            <path d={`M ${cx - 4} ${tipY + TIP_H} L ${cx} ${tipY + TIP_H + 5} L ${cx + 4} ${tipY + TIP_H} Z`} fill={C_INK} />
            <text
              x={cx}
              y={tipY + 12.5}
              textAnchor="middle"
              fill={C_PAPER}
              fontSize="9.5"
              fontWeight="500"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {money(t.ppm)}
            </text>
          </g>
        );
      })}

      <style>{`
        /* 7s loop:
             2-26%   the towers build up, left to right, 4% apart
             28-44%  stop 1 picked (tooltip springs in)
             48-64%  stop 2
             68-88%  stop 3, the cheapest per m², held longest
             90-96%  everything fades; 96-100% quiet pause */
        @keyframes rsRise0 {
          0%, 2%    { opacity: 0; transform: scaleY(0); }
          10%       { opacity: 1; transform: scaleY(1.05); }
          14%, 90%  { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes rsRise1 {
          0%, 6%    { opacity: 0; transform: scaleY(0); }
          14%       { opacity: 1; transform: scaleY(1.05); }
          18%, 90%  { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes rsRise2 {
          0%, 10%   { opacity: 0; transform: scaleY(0); }
          18%       { opacity: 1; transform: scaleY(1.05); }
          22%, 90%  { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        @keyframes rsRise3 {
          0%, 14%   { opacity: 0; transform: scaleY(0); }
          22%       { opacity: 1; transform: scaleY(1.05); }
          26%, 90%  { opacity: 1; transform: scaleY(1); }
          96%, 100% { opacity: 0; transform: scaleY(1); }
        }
        /* A pick lifts the tower from pale to solid; the last one lets go
           with the fade at the seam, so its name bar fades with it. */
        @keyframes rsPick0 {
          0%, 28%   { opacity: 0.35; }
          31%, 44%  { opacity: 1; }
          47%, 100% { opacity: 0.35; }
        }
        @keyframes rsPick1 {
          0%, 48%   { opacity: 0.35; }
          51%, 64%  { opacity: 1; }
          67%, 100% { opacity: 0.35; }
        }
        @keyframes rsPick2 {
          0%, 68%   { opacity: 0.35; }
          71%, 90%  { opacity: 1; }
          96%, 100% { opacity: 0.35; }
        }
        @keyframes rsTip0 {
          0%, 29%   { opacity: 0; transform: translateY(5px) scale(0.9); }
          33%, 42%  { opacity: 1; transform: translateY(0) scale(1); }
          46%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        @keyframes rsTip1 {
          0%, 49%   { opacity: 0; transform: translateY(5px) scale(0.9); }
          53%, 62%  { opacity: 1; transform: translateY(0) scale(1); }
          66%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        @keyframes rsTip2 {
          0%, 69%   { opacity: 0; transform: translateY(5px) scale(0.9); }
          73%, 86%  { opacity: 1; transform: translateY(0) scale(1); }
          90%, 100% { opacity: 0; transform: translateY(5px) scale(0.9); }
        }
        .rs-pick   { opacity: 0.35; }
        .rs-pick-0 { animation: rsPick0 7s ease-out infinite; }
        .rs-pick-1 { animation: rsPick1 7s ease-out infinite; }
        .rs-pick-2 { animation: rsPick2 7s ease-out infinite; }
        .rs-rise   { transform-box: fill-box; transform-origin: center bottom; }
        .rs-rise-0 { animation: rsRise0 7s cubic-bezier(.22,1,.36,1) infinite; }
        .rs-rise-1 { animation: rsRise1 7s cubic-bezier(.22,1,.36,1) infinite; }
        .rs-rise-2 { animation: rsRise2 7s cubic-bezier(.22,1,.36,1) infinite; }
        .rs-rise-3 { animation: rsRise3 7s cubic-bezier(.22,1,.36,1) infinite; }
        .rs-tip    { transform-box: fill-box; transform-origin: center bottom; opacity: 0; }
        .rs-tip-0  { animation: rsTip0 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        .rs-tip-1  { animation: rsTip1 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        .rs-tip-2  { animation: rsTip2 7s cubic-bezier(0.34,1.56,0.64,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .rs-rise-0, .rs-rise-1, .rs-rise-2, .rs-rise-3,
          .rs-pick-0, .rs-pick-1, .rs-pick-2,
          .rs-tip-0, .rs-tip-1, .rs-tip-2 { animation: none; }
          .rs-rise { opacity: 1; transform: none; }
          .rs-pick-2, .rs-tip-2 { opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

export default memo(ResidencesAnimation);
